import { $createLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $findMatchingParent } from '@lexical/utils';
import { $createTextNode, $getSelection, $insertNodes, $isRangeSelection, $isTextNode } from 'lexical';
import { Link as LinkIcon, Unlink } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fieldClasses, primaryButtonClasses } from '../../ui/styles';
import { ToolbarMenu } from '../../ui/ToolbarMenu';

const LINK_ATTRIBUTES = { target: '_blank', rel: 'noopener noreferrer' };

/**
 * Insert/edit a hyperlink. Mirrors Froala's `insertLink` popup, including its `linkAlwaysBlank`
 * setting — every link is opened in a new tab.
 */
export const LinkControl = ({ isLink, disabled }: { isLink: boolean; disabled: boolean }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');

  /** The link being edited, or else the selected text as the default display text. */
  const readInitial = useCallback(() => {
    const initial = { url: '', text: '' };
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      const link = $findMatchingParent(selection.anchor.getNode(), (node) => $isLinkNode(node));
      if ($isLinkNode(link)) {
        initial.url = link.getURL();
        initial.text = link.getTextContent();
      } else {
        initial.text = selection.getTextContent();
      }
    });
    return initial;
  }, [editor]);

  return (
    <ToolbarMenu icon={LinkIcon} caret="none" active={isLink} disabled={disabled} label={t('editor.toolbar.insert-link')}>
      {(close) => <LinkForm initial={readInitial()} isLink={isLink} close={close} />}
    </ToolbarMenu>
  );
};

type LinkFormProps = { initial: { url: string; text: string }; isLink: boolean; close: () => void };

const LinkForm = ({ initial, isLink, close }: LinkFormProps) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('common');
  const [url, setUrl] = useState(initial.url);
  const [text, setText] = useState(initial.text);

  const apply = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    // Blank text falls back to the URL, the way Froala does
    const label = text.trim() || trimmed;
    const textChanged = label !== initial.text;

    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (isLink) {
        const link = $findMatchingParent(selection.anchor.getNode(), (node) => $isLinkNode(node));
        if (!$isLinkNode(link)) return;
        link.setURL(trimmed);
        if (!textChanged) return;

        // Keep the formatting of the link's first run (e.g. bold) on the new text
        const first = link.getFirstDescendant();
        const replacement = $createTextNode(label);
        if ($isTextNode(first)) replacement.setFormat(first.getFormat()).setStyle(first.getStyle());
        link.clear().append(replacement);
        replacement.select();
        return;
      }

      // Unchanged text links the selection in place, keeping its formatting across blocks
      if (!selection.isCollapsed() && !textChanged) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, { url: trimmed, ...LINK_ATTRIBUTES });
        return;
      }

      // Otherwise the link replaces the selection (or is inserted at the caret)
      const link = $createLinkNode(trimmed, LINK_ATTRIBUTES);
      link.append($createTextNode(label));
      $insertNodes([link]);
    });
    close();
  };

  const submitOnEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    apply();
  };

  const remove = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    close();
  };

  return (
    <div className="flex w-64 flex-col gap-2 p-3">
      <label className="text-pbk-12 text-text-2" htmlFor="lexical-link-url">
        {t('editor.toolbar.link-url')}
      </label>
      <input
        id="lexical-link-url"
        autoFocus
        className={fieldClasses}
        value={url}
        placeholder="https://"
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={submitOnEnter}
      />
      <label className="text-pbk-12 text-text-2" htmlFor="lexical-link-text">
        {t('editor.toolbar.link-text')}
      </label>
      <input
        id="lexical-link-text"
        className={fieldClasses}
        value={text}
        placeholder={url.trim() || undefined}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={submitOnEnter}
      />
      <div className="flex items-center gap-2">
        <button type="button" className={primaryButtonClasses} disabled={!url.trim()} onClick={apply}>
          {t('editor.toolbar.apply')}
        </button>
        {isLink && (
          <button type="button" className="text-pbk-14 text-text-2 flex cursor-pointer items-center gap-1 px-1" onClick={remove}>
            <Unlink className="size-4" aria-hidden />
            {t('editor.toolbar.remove-link')}
          </button>
        )}
      </div>
    </div>
  );
};
